import { useEffect, useRef, type ReactNode } from "react";

import type { BoardRow } from "../board.ts";
import { StateBadge } from "./StateBadge.tsx";

/**
 * The design's row detail panel, which CLAUDE.md section 1 names among the things not to remove.
 *
 * `17 WP16 Tenant Admin.dc.html` opens one when any row of any page is clicked: a dialog with the
 * row's name, the page's title and source chip beneath it in Geist Mono, its state badge, a
 * `Details` table of the grid's own columns plus `State`, a `History` block of three entries, the
 * page's closing note repeated, and a footer of up to three commands.
 *
 * **Three things in it are the design's and two are not, and the two are findings rather than
 * choices.**
 *
 * *The `History` block is real on all four of these services.* The design's three rows are
 * `Created · 12 Aug 2026 · W. Mutua`, `Last changed · today 09:12 · K. Otieno` and `Version · v5`.
 * TEN-02, TEN-03, CUS-01 and PRC-01 each publish a created instant, a changed instant and an
 * integer version, so the block's shape is exactly supported — which is the only part of this
 * design all four contracts agree on. What none of them publishes is **who**: no actor is recorded
 * on a configuration entry, an entitlement, a customer profile or a rate card, so the design's two
 * names have no source and the entries carry the instant alone. Q-107.
 *
 * *The two state-dependent commands are drawn and refused.* The design maps a state class to an
 * action — `human` to `Open approval`, `caution` to `Fix issue`, `closed` to `Restore` — and offers
 * `Edit` wherever the page has a form. Neither has an operation on any of these five pages: the
 * approval the pricing board would open records no approver (Q-106), and every one of the five
 * forms requires a field with nothing on the wire to write it to. So each is drawn in the design's
 * geometry, disabled, with the measured reason on it — the same call the four screens built before
 * these five made on their primary commands.
 *
 * *The person branch is not drawn.* That file gives `staff` and `customers` a 64px circular photo
 * slot in place of the glyph tile. CUS-01 publishes no photo and no asset reference of any kind, so
 * the customers panel takes the glyph branch: a placeholder image is placeholder content, which
 * CLAUDE.md section 1 forbids in the same sentence as inventing a section. Q-103 records the form's
 * `Profile photo` field from the same absence.
 */

export interface RowDetailProps {
  readonly row: BoardRow;
  /** The grid's column headings, which the `Details` table repeats in the design's order. */
  readonly columns: readonly string[];
  /** The design's `PAGES[0] + ' · ' + PAGES[1]`: the page's title and its source chip. */
  readonly subtitle: string;
  /** The service whose lifecycle the badge belongs to. */
  readonly source: string;
  readonly note: string;
  /**
   * Whether the design draws an `Edit` in this panel — true unless said otherwise.
   *
   * The design offers `Edit` wherever the page has a form, and takes it away where there is none:
   * its own `formUI` returns `editS:{display:'none'}` on the `!F` branch. `platform · Event
   * catalogue and dead letters` is the first page here with no `FORMS` entry, so it is the first
   * to need this. Defaulted to drawn rather than required, because the five boards whose designs do
   * have a form should not have to say so to keep what they already have measured.
   */
  readonly editable?: boolean;
  readonly onClose: () => void;
}

/**
 * The command the design offers for a row in this state, and why this console cannot send it.
 *
 * The design's own map, in its own words, over the classes these five pages can reach. A class not
 * in the map draws no action, which is also the design's behaviour: its `actS` sets
 * `display: none`.
 */
const ACTION_BY_CLASS: Readonly<
  Partial<Record<BoardRow["state"]["classKey"], { label: string; glyph: string }>>
> = {
  human: { label: "Open approval", glyph: "approval" },
  failed: { label: "Retry now", glyph: "replay" },
  caution: { label: "Fix issue", glyph: "build" },
  waiting: { label: "Resend", glyph: "send" },
  closed: { label: "Restore", glyph: "restore" },
  active: { label: "View progress", glyph: "monitoring" },
};

export function RowDetail({
  row,
  columns,
  subtitle,
  source,
  note,
  editable,
  onClose,
}: RowDetailProps): ReactNode {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /*
     * Escape closes it, and the focus starts inside it.
     *
     * Neither is in the design file, which binds only a click on the scrim and on its two close
     * controls. A modal a keyboard cannot leave is the dispatch console's own finding about its
     * exception detail, and the fix is three lines rather than a decision.
     */
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    panel.current?.focus();
    return (): void => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const action = ACTION_BY_CLASS[row.state.classKey];

  return (
    <div
      className="detail"
      /*
       * The design's scrim, which dismisses on a click and is inert to assistive technology — the
       * same shape as this console's drawer scrim, and for the same reason: it is a pointer
       * affordance, not a named control. Escape is the keyboard's route out.
       */
      aria-hidden="true"
      onClick={onClose}
    >
      <div
        className="detail__panel"
        role="dialog"
        aria-modal="true"
        aria-label={row.cells[0]?.text ?? "Record"}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="detail__head">
          <span aria-hidden="true" className="detail__glyph">
            {row.glyph}
          </span>
          <span className="detail__titles">
            <strong className="detail__title">{row.cells[0]?.text ?? "—"}</strong>
            <span className="detail__sub">{subtitle}</span>
            <StateBadge
              code={source}
              status={row.state.label}
              classKey={row.state.classKey}
              label={row.state.label}
              {...(row.state.title === undefined ? {} : { title: row.state.title })}
            />
          </span>
          <button type="button" className="detail__close" aria-label="Close" onClick={onClose}>
            <span aria-hidden="true">close</span>
          </button>
        </div>

        <div className="detail__body">
          <div className="detail__section">
            <span className="detail__section-name">Details</span>
            <div className="detail__fields">
              {columns.map((column, index) => {
                const cell = row.cells[index];
                return (
                  <div key={column} className="detail__field">
                    <span className="detail__field-name">{column}</span>
                    <span
                      className={`detail__field-value${cell?.mono === true ? " detail__field-value--mono" : ""}${cell?.absent === true ? " detail__field-value--absent" : ""}`}
                    >
                      {cell === undefined || cell.absent === true ? "—" : cell.text}
                    </span>
                  </div>
                );
              })}
              {/* The design's own extra line: every panel ends its field table with the state. */}
              <div className="detail__field">
                <span className="detail__field-name">State</span>
                <span className="detail__field-value">{row.state.label}</span>
              </div>
              {row.detail.map((field) => (
                <div key={field.label} className="detail__field">
                  <span className="detail__field-name">{field.label}</span>
                  <span
                    className={`detail__field-value${field.mono === true ? " detail__field-value--mono" : ""}${field.absent === true ? " detail__field-value--absent" : ""}`}
                  >
                    {field.absent === true ? "—" : field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="detail__section">
            <span className="detail__section-name">History</span>
            <div className="detail__history">
              {row.history.map((entry) => (
                <div key={entry.label} className="detail__history-entry">
                  <span aria-hidden="true" className="detail__history-dot" />
                  <span className="detail__history-text">
                    <span className="detail__history-label">{entry.label}</span>
                    <span
                      className={
                        entry.absent === true
                          ? "detail__history-value detail__field-value--absent"
                          : "detail__history-value"
                      }
                    >
                      {entry.absent === true ? "—" : entry.value}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="detail__note">
            <span aria-hidden="true" className="detail__note-glyph">
              info
            </span>
            <span>{note}</span>
          </div>
        </div>

        <div className="detail__foot">
          <button type="button" className="detail__secondary" onClick={onClose}>
            Close
          </button>
          {action === undefined ? null : (
            <button
              type="button"
              className="detail__secondary"
              disabled
              title={`No operation a browser can call answers "${action.label}" for this row. Each of this console's boards draws a command its owning service publishes under /internal/v1 only, and two of them additionally need an approver the design's form has no field for: Q-140, Q-151, Q-153, Q-188.`}
            >
              <span aria-hidden="true" className="detail__action-glyph">
                {action.glyph}
              </span>
              {action.label}
            </button>
          )}
          {editable === false ? undefined : (
            <button
              type="button"
              className="detail__primary"
              disabled
              title="Every form this console draws presses a command published under /internal/v1 only, which the gateway does not expose, so none is offered. Each board's own module carries the refusal it puts on its primary button: Q-140, Q-151, Q-153, Q-188."
            >
              <span aria-hidden="true" className="detail__action-glyph">
                edit
              </span>
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
